import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'

import { auditApi } from './api'
import type { AuditEvent, AuditEventFilters } from './types'

const PAGE_SIZE = 50

/**
 * Settings > Audit log, for Owner and Administrator only (`settings.manage`).
 *
 * The story asks that a launch chain be legible — who, which website, which
 * WordPress user id, consumed or refused — without the viewer knowing what a
 * "launch" is. `DetailsList` renders whatever `details` an event carries, so
 * a launch-mint/consume/refusal event (`ms-w2-auth`'s to emit) is legible the
 * moment it exists, with no viewer-side change needed when it lands.
 */
export function AuditLogPage() {
  const [filters, setFilters] = useState<AuditEventFilters>({})
  const [offset, setOffset] = useState(0)

  const events = useQuery({
    queryKey: ['audit-events', filters, offset],
    queryFn: () => auditApi.events(filters, { limit: PAGE_SIZE, offset }),
  })

  function updateFilter(key: keyof AuditEventFilters, value: string) {
    setOffset(0)
    setFilters((current) => {
      const next = { ...current }
      if (value) next[key] = value
      else delete next[key]
      return next
    })
  }

  if (events.isError) {
    const refused = events.error instanceof ApiError && events.error.status === 403
    return (
      <p role="alert">
        {refused
          ? 'You need Owner or Administrator access to view the audit log.'
          : 'Could not load the audit log.'}
      </p>
    )
  }

  return (
    <section aria-label="Audit log">
      <h2>Audit log</h2>
      <form aria-label="Filter audit events" onSubmit={(event) => event.preventDefault()}>
        <label>
          Actor
          <input
            type="text"
            value={filters.actor ?? ''}
            onChange={(event) => updateFilter('actor', event.target.value)}
          />
        </label>
        <label>
          Action
          <input
            type="text"
            value={filters.action ?? ''}
            onChange={(event) => updateFilter('action', event.target.value)}
          />
        </label>
        <label>
          From
          <input
            type="datetime-local"
            value={filters.since ?? ''}
            onChange={(event) => updateFilter('since', event.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="datetime-local"
            value={filters.until ?? ''}
            onChange={(event) => updateFilter('until', event.target.value)}
          />
        </label>
        <a href={auditApi.exportUrl(filters)}>Export CSV</a>
      </form>

      {events.isPending ? <p>Loading…</p> : null}

      {events.data ? (
        <>
          {events.data.items.length === 0 ? (
            <p>No matching audit events.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Action</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Website</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {events.data.items.map((row) => (
                  <AuditEventRow key={row.id} event={row} />
                ))}
              </tbody>
            </table>
          )}
          <div className="audit-log__pagination">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!events.data.has_more}
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
    </section>
  )
}

function AuditEventRow({ event }: { event: AuditEvent }) {
  return (
    <tr>
      <td>{new Date(event.occurred_at).toLocaleString('en-GB')}</td>
      <td>{event.action}</td>
      <td>
        {event.actor}
        {event.impersonated_by ? ` (impersonated by ${event.impersonated_by})` : ''}
      </td>
      <td>{event.website_id ?? '—'}</td>
      <td>
        <DetailsList details={event.details} />
      </td>
    </tr>
  )
}

function DetailsList({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details)
  if (entries.length === 0) return <span>—</span>
  return (
    <dl className="audit-log__details">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{typeof value === 'string' ? value : JSON.stringify(value)}</dd>
        </div>
      ))}
    </dl>
  )
}
